import type { StudioApp } from './studio-api';
import { priceUnit, safeMediaUrl } from './studio-content';
import { BEFORE_KINDS, type StudioBeforeKind } from './studio-preview-template';

export interface StudioPreviewMediaSource {
  url: string;
  type: 'image' | 'video';
  poster?: string;
}
/** Роль сцены (§6.3 cast[]): подпись строки на листе и то, по чему лист рисует круг роли в слое «до» и считает roleSub */
export interface StudioPreviewRole {
  id: string;
  name: string;
  subtitle?: string;
  required?: boolean;
  kinds?: string[];
  gender?: 'm' | 'f' | null;
  min?: number;
  max?: number;
  allowChar?: boolean;
}
export interface StudioPreviewModel {
  app: StudioApp;
  title: string;
  plot?: string;
  format?: string;
  price?: { amount: number | string; unit?: string; from: boolean };
  before?: StudioPreviewMediaSource;
  after?: StudioPreviewMediaSource;
  beforeLabel?: string;
  afterLabel?: string;
  /** вид слоя «до» (preview.before) */
  beforeKind?: StudioBeforeKind;
  roles: StudioPreviewRole[];
  /** строки «Что нужно» (need.rows); нет поля — строки по формату, как у прототипа (needRows) */
  needs?: Array<{ icon?: string; title: string; hint?: string }>;
  needText?: string;
  meta?: string;
  stylesCount?: number;
  /** длительность ролика (duration_s) — подвал карточки по формату */
  duration?: number;
  /** вход экрана с ролью в фокусе (entry.open_role): кнопка «Выбрать, кто играет»; нет поля — по формату */
  openRole?: boolean;
  /** собран из карточки, документ шаблона ещё в пути */
  provisional?: boolean;
}
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const text = (value: unknown, max = 1000): string | undefined =>
  typeof value === 'string' && value.trim() && value.length <= max ? value : undefined;
const count = (value: unknown, max: number): number | undefined =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 && value <= max ? value : undefined;
export function studioMediaUrl(value: unknown) {
  return safeMediaUrl(value, 4096) ?? undefined;
}
/** «Ваше фото» самого приложения (BotCore.YOU шаблона — public/assets/you-f.webp, [ДЕМО], Q-299): слой «до» и круг «вы» листа,
    когда документ своего не прислал (§6.3: before_url: null) или его адрес не прошёл проверку. Адрес не из документа: того же
    источника, что приложение (у сборки картинки набора — blob: этого же источника, window.ASSET), иначе — та же проверка
    safeMediaUrl, что у адресов документа */
export function studioSampleYou(): string | undefined {
  const own = window.BotCore?.YOU;
  try {
    const url = new URL(String(typeof own === 'function' ? (own as () => unknown)() : 'assets/you-f.webp'), document.baseURI);
    return url.origin === location.origin && /^(https?|blob):$/.test(url.protocol) && !url.username && !url.password
      ? url.href
      : studioMediaUrl(url.href);
  } catch {
    return undefined;
  }
}
export function studioMedia(value: unknown, mime?: unknown, poster?: unknown): StudioPreviewMediaSource | undefined {
  const url = studioMediaUrl(value);
  if (!url) return undefined;
  const path = new URL(url).pathname.toLowerCase();
  // Тип берётся из опубликованного MIME либо явного расширения. Непроверенный URL не выдаём за видео.
  const video = mime === 'video/mp4' || mime === 'video/webm' || mime === 'video' || /\.(mp4|webm)$/.test(path);
  return { url, type: video ? 'video' : 'image', poster: studioMediaUrl(poster) };
}
/** Лист из того, что уже есть на карточке (название, обложка, цена, формат), пока документ шаблона в пути: лист выезжает заполненным,
    как у шаблона, а не полоской «Загружаем…». Действий у такого листа нет — «Попробовать» ждёт документа */
export function provisionalPreview(item: Record<string, unknown>): StudioPreviewModel | null {
  const title = text(item.name, 200);
  if (!title || !Number.isSafeInteger(item.id)) return null;
  const price = object(item.price) ? item.price : { amount: item.price };
  const amount = typeof price.amount === 'number' && Number.isFinite(price.amount) && price.amount >= 0 ? price.amount : undefined;
  const preview = object(item.preview) ? item.preview : {};
  return {
    app: item as unknown as StudioApp,
    title,
    format: text(item.format, 64),
    price: amount === undefined ? undefined : { amount, unit: text(priceUnit(price), 64), from: price.from === true },
    after: studioMedia(preview.after_url ?? item.cover_url ?? item.cover ?? (Array.isArray(item.covers) ? item.covers[0] : undefined)),
    roles: [],
    needs: [],
    provisional: true,
  };
}
export function studioPreview(value: unknown, expectedId: number): StudioPreviewModel {
  if (
    !Number.isSafeInteger(expectedId) ||
    expectedId <= 0 ||
    !object(value) ||
    value.id !== expectedId ||
    !text(value.name, 200) ||
    !object(value.entry) ||
    !text(value.entry.screen, 64)
  )
    throw new Error('studio_preview_invalid');
  const preview = object(value.preview) ? value.preview : {};
  const need = object(value.need) ? value.need : {};
  const price = object(value.price) ? value.price : { amount: value.price };
  const amount =
    typeof price.amount === 'number' && Number.isFinite(price.amount) && price.amount >= 0
      ? price.amount
      : typeof price.amount === 'string' && /^\d{1,12}(\.\d{1,8})?$/.test(price.amount)
        ? price.amount
        : undefined;
  const roles = Array.isArray(value.cast)
    ? value.cast
        .slice(0, 20)
        .filter(object)
        .flatMap((role): StudioPreviewRole[] => {
          const id = text(role.id, 128),
            name = text(role.name, 200);
          const kinds = Array.isArray(role.kinds)
            ? role.kinds.slice(0, 8).flatMap(kind => (text(kind, 32) ? [kind as string] : []))
            : undefined;
          return id && name
            ? [
                {
                  id,
                  name,
                  subtitle: text(role.subtitle, 200),
                  required: typeof role.required === 'boolean' ? role.required : undefined,
                  kinds,
                  gender: role.gender === 'm' ? 'm' : role.gender === 'f' ? 'f' : role.gender === null ? null : undefined,
                  min: count(role.min, 20),
                  max: count(role.max, 20),
                  allowChar: typeof role.allow_char === 'boolean' ? role.allow_char : undefined,
                },
              ]
            : [];
        })
    : [];
  const needs = Array.isArray(need.rows)
    ? need.rows
        .slice(0, 20)
        .filter(object)
        .flatMap(row => {
          const title = text(row.title, 200);
          return title ? [{ icon: text(row.icon, 64), title, hint: text(row.hint) }] : [];
        })
    : undefined;
  const openRole = value.entry.open_role;
  return {
    app: value as unknown as StudioApp,
    title: value.name as string,
    plot: text(value.plot, 4000),
    format: text(value.format, 64),
    price: amount === undefined ? undefined : { amount, unit: text(priceUnit(price), 64), from: price.from === true },
    before: studioMedia(preview.before_url, preview.before_mime ?? preview.before_type),
    after: studioMedia(
      preview.after_url ?? value.cover_url ?? value.cover,
      preview.after_mime ?? preview.mime ?? preview.type,
      preview.poster_url,
    ),
    beforeLabel: text(preview.before_label, 200),
    afterLabel: text(preview.after_label, 200),
    beforeKind: BEFORE_KINDS.find(kind => kind === preview.before),
    roles,
    needs,
    needText: text(need.text, 200),
    meta: text(need.meta, 200),
    duration: count(value.duration_s, 24 * 3600),
    openRole: openRole === true || !!text(openRole, 128) ? true : openRole === false ? false : undefined,
    stylesCount:
      typeof value.styles_count === 'number' && Number.isSafeInteger(value.styles_count) && value.styles_count >= 0
        ? value.styles_count
        : Array.isArray(value.styles)
          ? value.styles.length
          : undefined,
  };
}
